/**
 * Custom hook for fetching event data with SWR
 * Consolidates duplicated SWR fetching pattern from detail.tsx, donate.tsx, share.tsx
 */

import useSWR from "swr";
import { useRouter } from "next/router";
import { fetcher } from "@dreambigger/shared/src/api/api";
import { eventPaths } from "@dreambigger/shared/src/api/events";
import { organizationPaths } from "@dreambigger/shared/src/api/organizations";
import { communityPaths } from "@dreambigger/shared/src/api/communities";
import { api, tokenHelper } from "@dreambigger/shared/src/api/weserve/api-helper";
import { Event as EventType, Community, OrgBootstrap } from "@dreambigger/shared/src/types";
import { message } from "@dreambigger/design-system/src/components/";
import { getCoverImage } from "@dreambigger/shared/src/utils";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";

export interface UseEventDataProps {
  initialEvent: EventType;
  initialOrgBootstrap: OrgBootstrap;
  initialCommunity: Community;
  showErrorMessage?: boolean;
}

export interface UseEventDataResult {
  event: EventType;
  orgBootstrap: OrgBootstrap;
  community: Community;
  coverImage: string;
  hasImages: boolean;
  isFallback: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook that provides event data with SWR caching and revalidation
 * Falls back to static props data when SWR data is not available
 */
export const useEventData = ({
  initialEvent,
  initialOrgBootstrap,
  initialCommunity,
  showErrorMessage = true,
}: UseEventDataProps): UseEventDataResult => {
  const { isFallback } = useRouter();
  const fetcherFn = (url: string) => fetcher(api, tokenHelper, url);

  // Fetch event data
  const {
    data: eventData,
    error: eventError,
    isValidating: eventLoading,
  } = useSWR<EventType>(
    initialEvent?.id ? eventPaths.getById(initialEvent.id) : null,
    fetcherFn,
    {
      fallbackData: initialEvent,
      onError: (error) => {
        console.error("Error loading event:", error);
        if (showErrorMessage) {
          message.error("Something went wrong loading this event.");
        }
      },
    }
  );

  // Fetch orgBootstrap data
  const { data: orgBootstrapData, isValidating: orgLoading } = useSWR<OrgBootstrap>(
    initialEvent?.organization?.id
      ? organizationPaths.getOrgBootstrapPath(initialEvent.organization.id)
      : null,
    fetcherFn,
    { fallbackData: initialOrgBootstrap }
  );

  // Fetch community data
  const { data: communityData, isValidating: communityLoading } = useSWR<Community>(
    initialEvent?.organization?.community?.slug
      ? communityPaths.getBySlug(initialEvent.organization.community.slug)
      : null,
    fetcherFn,
    { fallbackData: initialCommunity }
  );

  // Use fetched data or fall back to initial static data
  const event = eventData || initialEvent;
  const orgBootstrap = orgBootstrapData || initialOrgBootstrap;
  const community = communityData || initialCommunity;

  // Compute derived values
  const hasImages = Boolean(event?.images && event.images.length > 0);
  const coverImage =
    (hasImages && getCoverImage(event.images)) || cotributeDefaults.coverImage;

  return {
    event,
    orgBootstrap,
    community,
    coverImage,
    hasImages,
    isFallback,
    isLoading: eventLoading || orgLoading || communityLoading,
    isError: Boolean(eventError),
  };
};

export default useEventData;
